/**
 * ChangeRequestReviewDialog — native V2 review dialog for client
 * change requests (mirrors the legacy dialog at App.js ~45559 so V2
 * Tasks no longer bounce to /dashboard?view=notes&open_change_request=).
 *
 * Fetches GET /change-requests/{id} on open; approve / deny / dismiss
 * inline; "Open Client to Edit" delegates to the app-level
 * IntakeWizardProvider (no navigation).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, XCircle, Edit, Copy, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useIntakeWizard } from '../../contexts/IntakeWizardContext';
import { isSuperAdmin } from '../../utils/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ChangeRequestReviewDialog = ({ requestId, open, onOpenChange, user, onActioned }) => {
  const intakeWizard = useIntakeWizard();
  const [req, setReq] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !requestId) { setReq(null); return; }
    let cancelled = false;
    axios.get(`${API}/change-requests/${requestId}`)
      .then((r) => { if (!cancelled) setReq(r.data); })
      .catch(() => {
        if (!cancelled) {
          toast.error('Could not open that change request.');
          onOpenChange(false);
        }
      });
    return () => { cancelled = true; };
  }, [open, requestId, onOpenChange]);

  const canReview = req?.status === 'pending' && (
    isSuperAdmin(user) || user?.is_biller || user?.is_scheduler ||
    ['office', 'office staff', 'office_staff'].includes((user?.role || '').toLowerCase())
  );

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      onOpenChange(false);
      onActioned?.();
    } catch (e) {
      toast.error('Action failed — please try again');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="v2-change-request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Badge className={`${
              req?.status === 'pending' ? 'bg-blue-500' :
              req?.status === 'approved' ? 'bg-green-500' :
              req?.status === 'denied' ? 'bg-red-500' : 'bg-gray-500'
            } text-white`}>
              {req?.status?.toUpperCase() || 'PENDING'}
            </Badge>
            Change Request
          </DialogTitle>
          <DialogDescription>Review and take action on this change request</DialogDescription>
        </DialogHeader>

        {!req ? (
          <div className="flex items-center justify-center py-10 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Client</p>
                <p className="font-semibold" data-testid="v2-cr-client-name">{req.client_name}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Requested By</p>
                <p className="font-semibold">{req.requested_by_name}</p>
                <p className="text-xs text-gray-500">{new Date(req.created_at).toLocaleString()}</p>
              </div>
            </div>

            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-xs text-blue-600 mb-1">Category</p>
              <p className="font-semibold text-blue-900 capitalize">
                {req.field_category?.replace(/_/g, ' ') || 'General'}
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-medium text-gray-700">Requested Changes</p>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="text-gray-500 hover:text-gray-700"
                  onClick={() => {
                    const textToCopy = req.proposed_value || req.reason ||
                      (req.changes?.map(c => `${c.field_name}: ${c.new_value || c.proposed_value}`).join('\n')) ||
                      'No details';
                    navigator.clipboard.writeText(textToCopy);
                    toast.success('Copied to clipboard');
                  }}
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Copy
                </Button>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                {req.proposed_value ? (
                  <p className="text-sm whitespace-pre-wrap">{req.proposed_value}</p>
                ) : req.changes && req.changes.length > 0 ? (
                  <div className="space-y-2">
                    {req.changes.map((change, idx) => (
                      <div key={idx} className="border-b border-gray-200 pb-2 last:border-0 last:pb-0">
                        <p className="text-xs text-gray-500 capitalize">{change.field_name?.replace(/_/g, ' ')}</p>
                        <div className="flex items-center gap-2 text-sm">
                          {change.old_value && (
                            <>
                              <span className="text-red-600 line-through">{change.old_value}</span>
                              <span className="text-gray-400">→</span>
                            </>
                          )}
                          <span className="text-green-600 font-medium">{change.new_value || change.proposed_value || '(not specified)'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No details provided</p>
                )}
              </div>
            </div>

            {req.reason && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Reason</p>
                <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                  <p className="text-sm whitespace-pre-wrap text-amber-900">{req.reason}</p>
                </div>
              </div>
            )}

            {req.reviewed_by_name && (
              <div className={`p-3 rounded-lg ${req.status === 'approved' ? 'bg-green-50' : 'bg-red-50'}`}>
                <p className="text-xs text-gray-500 mb-1">
                  {req.status === 'approved' ? 'Approved' : 'Denied'} By
                </p>
                <p className="font-semibold">{req.reviewed_by_name}</p>
                {req.reviewed_at && (
                  <p className="text-xs text-gray-500">{new Date(req.reviewed_at).toLocaleString()}</p>
                )}
                {req.review_reason && <p className="text-sm mt-2 italic">"{req.review_reason}"</p>}
              </div>
            )}

            {canReview && (
              <div className="space-y-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={busy}
                  data-testid="v2-cr-open-client-btn"
                  onClick={() => {
                    if (req.client_id) {
                      onOpenChange(false);
                      intakeWizard.open(req.client_id);
                      toast.info('Make your changes, save, then find the change request in your Tasks to approve/deny.');
                    } else {
                      toast.error('Client not found');
                    }
                  }}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Open Client to Edit
                </Button>
                <div className="flex gap-2">
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    disabled={busy}
                    data-testid="v2-cr-approve-btn"
                    onClick={() => act(
                      () => axios.put(`${API}/change-requests/${req.id}/approve`),
                      'Change request approved',
                    )}
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Approve
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 border-red-600 text-red-600 hover:bg-red-50"
                    disabled={busy}
                    data-testid="v2-cr-deny-btn"
                    onClick={() => {
                      const reason = window.prompt('Please provide a reason for denying this request:');
                      if (reason) {
                        act(
                          () => axios.put(`${API}/change-requests/${req.id}/deny`, { reason }),
                          'Change request denied',
                        );
                      }
                    }}
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Deny
                  </Button>
                </div>
              </div>
            )}

            {req.status !== 'pending' && (
              <div className="pt-4 border-t">
                <Button
                  variant="outline"
                  className="w-full"
                  disabled={busy}
                  data-testid="v2-cr-dismiss-btn"
                  onClick={() => act(
                    () => axios.put(`${API}/change-requests/${req.id}/dismiss`),
                    'Dismissed',
                  )}
                >
                  Dismiss
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChangeRequestReviewDialog;
